import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, ContentCopy as CopyIcon, Autorenew as RegenIcon,
  Send as SendIcon
} from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PillTabs from 'components/shared/tabs/PillTabs';
import AppBarContext from 'context/AppBarContext';
import OrganizationMembersTab from 'components/admin-organizations/OrganizationMembersTab';
import OrganizationPlansForm from 'components/admin-organizations/OrganizationPlansForm';
import OrganizationImport from 'components/admin-organizations/OrganizationImport';
import OrganizationAssignBoard from 'components/admin-organizations/OrganizationAssignBoard';
import OrganizationWellbeingTab from 'components/admin-organizations/OrganizationWellbeingTab';
import OrganizationDepartmentsTab from 'components/admin-organizations/OrganizationDepartmentsTab';
import OrganizationReportsTab from 'components/admin-organizations/OrganizationReportsTab';
import OrganizationCoverField from 'components/admin-organizations/OrganizationCoverField';
import {
  ORGANIZATION, REGENERATE_ORGANIZATION_CODE, RESEND_ORGANIZATION_CODE
} from 'api/graphql/organization';
import routes from 'common/routes';

function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['organizations', 'routes']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const [tab, setTab] = useState(0);
  const [codeMsg, setCodeMsg] = useState('');
  // El estado del import vive aquí para que la lista/archivo persista al cambiar de tab.
  const [importState, setImportState] = useState({ rows: [], fileName: '', report: null });

  const { data, loading, refetch } = useQuery(ORGANIZATION, { variables: { id }, fetchPolicy: 'cache-and-network' });
  const [regenerateCode] = useMutation(REGENERATE_ORGANIZATION_CODE);
  const [resendCode] = useMutation(RESEND_ORGANIZATION_CODE);

  const org = data?.organization;

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(t('organizations', { ns: 'routes', defaultValue: 'Organizaciones' }));
  }, [setAppBarButton, setAppBarTitle, t]);

  const copyCode = () => { if (org) navigator.clipboard?.writeText(org.join_code).catch(() => {}); };
  const handleRegenerate = async () => {
    await regenerateCode({ variables: { id } });
    setCodeMsg(t('codeRegenerated', { defaultValue: 'Código regenerado y reenviado.' }));
    refetch();
  };
  const handleResend = async () => {
    const res = await resendCode({ variables: { id } });
    setCodeMsg(res?.data?.resendOrganizationCode
      ? t('codeResent', { defaultValue: 'Código reenviado al correo de contacto.' })
      : t('codeNoContact', { defaultValue: 'La organización no tiene correo de contacto.' }));
  };

  // Cada tab viaja con su propio panel: antes un `offset` calculado a mano mapeaba índice→panel
  // y cualquier tab nuevo o condicional lo desalineaba.
  const tabsConfig = useMemo(() => {
    if (!org) return [];
    return [
      {
        key: 'members',
        label: t('membersTab', { defaultValue: 'Miembros' }),
        render: () => (
          <OrganizationMembersTab
            organizationId={org.id}
            onChanged={refetch}
            showWellbeingActions={!org.is_default}
          />
        )
      },
      // Bienestar y Departamentos solo aplican a organizaciones reales (no a la default).
      ...(org.is_default ? [] : [
        {
          key: 'wellbeing',
          label: t('wellbeingTab', { defaultValue: 'Bienestar' }),
          render: () => <OrganizationWellbeingTab organizationId={org.id} organizationName={org.name} />
        },
        {
          key: 'departments',
          label: t('departmentsTab', { defaultValue: 'Departamentos' }),
          render: () => <OrganizationDepartmentsTab organizationId={org.id} />
        }
      ]),
      {
        key: 'plans',
        label: t('plansTab', { defaultValue: 'Planes y descuentos' }),
        render: () => (
          <OrganizationPlansForm
            organizationId={org.id}
            planDiscounts={org.plan_discounts}
            companyCovered={org.company_covered_percent}
            onSaved={refetch}
          />
        )
      },
      {
        key: 'import',
        label: t('importTab', { defaultValue: 'Importar' }),
        render: () => (
          <OrganizationImport
            organizationId={org.id}
            onImported={refetch}
            value={importState}
            onChange={setImportState}
          />
        )
      },
      // El histórico de reportes es parte de Bienestar: no aplica a la organización default.
      ...(org.is_default ? [] : [
        {
          key: 'reports',
          label: t('reportsTab', { defaultValue: 'Reporte de bienestar' }),
          render: () => <OrganizationReportsTab organizationId={org.id} />
        }
      ]),
      {
        key: 'assign',
        label: t('assignTab', { defaultValue: 'Asignar' }),
        render: () => <OrganizationAssignBoard organizationId={org.id} onAssigned={refetch} />
      }
    ];
  }, [org, t, refetch, importState]);

  // Si la organización cambia y el tab activo deja de existir, se cae al primero.
  const activeTab = tabsConfig[tab] ? tab : 0;

  if (loading && !org) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: 'gold.main' }} /></Box>;
  }
  if (!org) {
    return (
      <Stack spacing={2} alignItems="flex-start" sx={{ py: 4 }}>
        <Typography color="text.secondary">{t('notFound', { defaultValue: 'Organización no encontrada.' })}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(routes.adminOrganizations.path)} sx={{ textTransform: 'none' }}>
          {t('back', { defaultValue: 'Volver' })}
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <SEOPageTitle title={org.name} />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(routes.adminOrganizations.path)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
        {t('back', { defaultValue: 'Volver' })}
      </Button>

      <Box sx={{ p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={800}>{org.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {org.members_count} {t('members', { defaultValue: 'miembros' })}
              {org.is_default && ` · ${t('defaultOrg', { defaultValue: 'Organización por defecto' })}`}
            </Typography>
          </Box>
          {!org.is_default && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={org.join_code} sx={{ fontFamily: 'monospace', letterSpacing: 2, fontWeight: 700, backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)' }} />
              <Tooltip title={t('copy', { defaultValue: 'Copiar' })}><IconButton size="small" onClick={copyCode}><CopyIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
              <Tooltip title={t('resendCode', { defaultValue: 'Reenviar código' })}><IconButton size="small" onClick={handleResend}><SendIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
              <Tooltip title={t('regenerateCode', { defaultValue: 'Regenerar código' })}><IconButton size="small" onClick={handleRegenerate}><RegenIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
            </Stack>
          )}
        </Stack>
        {codeMsg && <Typography variant="caption" sx={{ color: 'var(--otx-gold-text)', mt: 1, display: 'block' }}>{codeMsg}</Typography>}
      </Box>

      {/* La portada solo tiene sentido en empresas reales: la organización por defecto no tiene
          panel propio al que ponerle fondo. */}
      {/* Dos imágenes distintas y a propósito: la portada llena el fondo del hero y el logo se
          muestra entero encima. Lo ven el gestor de bienestar Y todos los colaboradores de la
          empresa en su inicio; quien no pertenece a ninguna sigue con el degradado de OTRIX. */}
      {!org.is_default && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 2, minWidth: 0, width: '100%' }}>
            <OrganizationCoverField organizationId={org.id} coverUrl={org.cover_url} onChanged={refetch} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <OrganizationCoverField
              variant="logo"
              organizationId={org.id}
              coverUrl={org.logo_url}
              onChanged={refetch}
            />
          </Box>
        </Stack>
      )}

      <PillTabs
        value={activeTab}
        onChange={(_, v) => setTab(v)}
        ariaLabel={t('tabsLabel', { defaultValue: 'Secciones de la organización' })}
        sx={{ mb: 2.5 }}
        tabs={tabsConfig.map(({ key, label }) => ({ key, label }))}
      />

      {tabsConfig[activeTab]?.render()}
    </>
  );
}

export default OrganizationDetail;
